import {gql} from 'apollo-angular';
import {Injectable} from '@angular/core';
import {DemoService} from 'app/core/services/demo.service';
import {LocalStorageService} from 'app/core/services/local-storage.service';
import {CleanApiCacheGuard} from 'app/shared/modules/api/guards/clean-api-cache-guard.service';
import {ApiService} from 'app/shared/modules/api/services/api.service';
import jwt_decode from 'jwt-decode';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter, map, pluck, switchMap, take, tap} from 'rxjs/operators';
import {currentUser, currentUser_currentUser, currentUser_currentUser_permissions} from './gql/currentUser';
import {GraphQLError, GraphQLErrorType} from 'app/core/error-handler/errors/grahpql/graphql-error';
import {UserRoles} from 'app/core/helpers/user.roles';
import {cloneDeep} from 'app/core/helpers/object';
import {AuthorizationError} from 'app/core/error-handler/errors/authorization.error';
import {signUp, signUp_signUp, signUpVariables} from 'app/core/services/gql/signUp';
import {signInV2, signInV2_signInV2_session, signInV2Variables} from 'app/core/services/gql/signInV2';
import {ThemeCustomizationService} from 'app/core/services/theme-customization.service';
import {refreshToken as refreshTokenI} from './gql/refreshToken';
import {currentFAQUrl} from 'app/core/services/gql/currentFAQUrl';
import {demoSignIn} from './gql/demoSignIn';
import {remindPassword} from './gql/remindPassword';
import {resendConfirmationEmail} from './gql/resendConfirmationEmail';
import {AuthServiceHelper} from 'app/core/services/auth.service.helper';
import {TwoFactorChallengeType} from '../../../globalTypes';
import {FetchPolicy} from '@apollo/client';

const currentUserQuery = gql`
    query currentUser {
        currentUser {
            id
            name
            email
            roles
            apiKey
            locale
            createdAt
            balance
            availableBalance
            isDemoAccount
            intercomSign
            wlDomainId
            rank
            timezone
            themeMode
            permissions {
                canCreateImmediatePayoutRequests
                canCreatePayoutRequests
                canUseCryptoPaymentSystems
                canCreateApps
                canManageNetworkAccounts
                canViewDashboard
                canViewInternalDashboard
                canViewForecastHistory
                canViewDashboardLtv
                canViewDashboardRetentionRate
                canViewWhiteLabel
                canViewApps
                canViewSegments
                canViewAdServer
                canViewPayouts
                canViewOldDashboard
                canViewSdkDocs
                canViewIntegrations
                canViewFaq
                canViewAdminPanel
                canViewAmbassadorProgram
                canViewBidmanager
                canViewBidonInDcc
                canViewEmbeddedBidonUi
                bidManagerPermissions {
                    canManageUaAccounts
                    canViewBidmanager
                    canManageCampaigns
                    canManageCreatives
                    canManageTargetCpaCampaigns
                }
                canViewPublishersDashboard
                canViewNewNavigationMenu
                canViewNewMediationSettings
                canViewMultiUserStats
                canViewFunnels
                canAddCreativeDesigner
                canViewExperiments
                canViewMultiMediationStats
                canToggleMultiMediationStats
                canViewExperimentalFrontend
                canViewGdprSetting
                canManageConfigurationsInDcc
            }
            currentNotifications {
                id
                global
                body
                status
            }
            notifications {
                nodes {
                    canBeDeleted
                    createdAt
                    id
                    isRead
                    subject
                    url
                }
                pageInfo {
                    currentPage
                    pageSize
                    totalPages
                }
                totalCount
                unreadCount
            }
            isSdkIntegrated
            isSdkOutdated
            isTermsChecked
            hasSupportRole
            hadAppsEver
            hasPublishedApp
            isActiveAccount
            isFirstPaymentCompleted
            googleConversionTrackingEvents
            dashboardSettings
            defaultMainPage
            onboardingShowCreateApp
        }
    }
`;
const faqUrlQuery = gql`
    query currentFAQUrl {
        currentFAQUrl
    }
`;

export type CurrentUser = currentUser_currentUser;
export type UserPermissions = currentUser_currentUser_permissions;

export const signInV2Mutation = gql`
    mutation signInV2($email: String!, $password: String!, $twoFactorEmailCode: String, $twoFactorOneTimePassword: String) {
        signInV2(
            email: $email
            password: $password
            twoFactorEmailCode: $twoFactorEmailCode
            twoFactorOneTimePassword: $twoFactorOneTimePassword
        ) {
            session {
                accessToken
                refreshToken
            }
            twoFactorChallengeType
        }
    }
`;

export const demoSignInMutation = gql`
    mutation demoSignIn {
        demoSignIn {
            accessToken
            refreshToken
        }
    }
`;

export const refreshTokenMutation = gql`
    mutation refreshToken($token: String!) {
        refreshToken(token: $token) {
            accessToken
            refreshToken
        }
    }
`;

export const signOutMutation = gql`
    mutation signOut {
        signOut
    }
`;

export const remindPasswordMutation = gql`
    mutation remindPassword($email: String!) {
        remindPassword(email: $email) {
            onModeration
        }
    }
`;

export const resendConfirmationEmailMutation = gql`
    mutation resendConfirmationEmail($email: String!) {
        resendConfirmationEmail(email: $email) {
            onModeration
        }
    }
`;

export const signUpMutation = gql`
    mutation signUp(
        $email: String!
        $password: String!
        $name: String!
        $locale: String!
        $marketingData: MarketingData
        $terms: Boolean!
        $education: Boolean!
        $productUpdatesSubscribe: Boolean!
        $shareToken: String
        $registrationSource: String
        $affiliatedBy: ID
        $referer: String
        $utmCampaign: String
        $utmSource: String
        $confirmationEmailToken: String
    ) {
        signUp(
            email: $email
            password: $password
            name: $name
            locale: $locale
            marketingData: $marketingData
            terms: $terms
            education: $education
            productUpdatesSubscribe: $productUpdatesSubscribe
            shareToken: $shareToken
            registrationSource: $registrationSource
            affiliatedBy: $affiliatedBy
            referer: $referer
            utmCampaign: $utmCampaign
            utmSource: $utmSource
            confirmationEmailToken: $confirmationEmailToken
        ) {
            onModeration
            session {
                accessToken
                refreshToken
            }
        }
    }
`;

function minutesToMiliSeconds(minutes: number): number {
    return minutes * 60 * 1000;
}

@Injectable()
export class TokenRefresher {
    constructor(
        private api: ApiService,
        private localStorage: LocalStorageService
    ) {}

    timerID;
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    checkInterval = minutesToMiliSeconds(5 + Math.random() * 5); // once per 10 minute ms +- some random time
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    refreshAfter = 24 * 60 * 60; // s
    // eslint-enable-next-line no-magic-numbers

    run() {
        console.debug('[TokenRefresher] run');
        clearInterval(this.timerID);
        this.timerID = setInterval(() => this.check(), this.checkInterval);
    }

    check() {
        console.debug('[TokenRefresher] check');
        const accessTokenRaw = this.localStorage.getItem('apd_access_token');
        const refreshTokenRaw = this.localStorage.getItem('apd_refresh_token');
        if (!accessTokenRaw || !refreshTokenRaw) {
            return;
        }

        let accessToken;
        let refreshToken;
        try {
            accessToken = jwt_decode<{exp: number}>(accessTokenRaw);
            refreshToken = jwt_decode<{iat: number; exp: number}>(refreshTokenRaw);
        } catch (e) {
            console.debug('Failed to decode tokens ', e);
            return;
        }
        const secondsNow = Date.now() / 1000;
        if (secondsNow > accessToken.exp || secondsNow > refreshToken.exp) {
            return;
        }

        if (this.refreshAfter + refreshToken.iat < secondsNow) {
            return this.refreshAccessToken(refreshTokenRaw);
        }
    }

    refreshAccessToken(refreshTokenRaw: string) {
        return this.api
            .mutate<refreshTokenI>({
                mutation: refreshTokenMutation,
                variables: {token: refreshTokenRaw},
                skipBeforeMutationValidation: true,
                context: {
                    skipBatch: true,
                    disableErrorNotifier: true,
                },
            })
            .toPromise()
            .then(r => r.refreshToken)
            .then(
                ({accessToken, refreshToken}) => {
                    this.localStorage.setItem('apd_access_token', accessToken);
                    this.localStorage.setItem('apd_refresh_token', refreshToken);
                },
                err => {
                    if (
                        err instanceof GraphQLError &&
                        err.is(GraphQLErrorType.NotFound) &&
                        this.localStorage.getItem('apd_refresh_token') === refreshTokenRaw
                    ) {
                        console.debug('[TokenRefresher] Refresh Token NotFound. Token removed');
                        this.localStorage.removeItem('apd_refresh_token');
                    }
                    console.log('Failed to refresh token', err);
                }
            );
    }

    stop() {
        clearInterval(this.timerID);
    }
}

@Injectable()
export class AuthService {
    public currentUser$: Observable<CurrentUser> = new BehaviorSubject<CurrentUser>(null);
    public loggedIn$: Observable<boolean> = this.currentUser$.pipe(map(x => !!x));
    public loggedOut$: Observable<boolean> = this.loggedIn$.pipe(map(x => !x));

    public currentBalance$: Observable<number> = this.currentUser$.pipe(pluck('balance'));
    public currentFAQUrl$: Observable<string> = new BehaviorSubject<string>(null);

    twoFaEmailCode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    twoFaOneTimePassword$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public currentUserId: string;
    private loggedId;
    private currentUser;
    initError?: Error;

    private afterSignOut: (() => void)[] = [];

    get canViewMultiUserStats() {
        return this.getCurrentUserLastValue().permissions.canViewMultiUserStats;
    }

    get canViewMultiMediationStats() {
        return this.getCurrentUserLastValue().permissions.canViewMultiMediationStats;
    }

    get canToggleMultiMediationStats() {
        return this.getCurrentUserLastValue().permissions.canToggleMultiMediationStats;
    }

    get canViewNewMediationSettings() {
        return this.getCurrentUserLastValue().permissions.canViewNewMediationSettings;
    }

    get canViewBidonInDcc() {
        return this.getCurrentUserLastValue().permissions.canViewBidonInDcc;
    }

    get canManageConfigurationsInDcc() {
        return this.getCurrentUserLastValue().permissions.canManageConfigurationsInDcc;
    }

    constructor(
        private api: ApiService,
        private demo: DemoService,
        private cleanApiCache: CleanApiCacheGuard,
        private localStorage: LocalStorageService,
        private refresher: TokenRefresher,
        private themeCustom: ThemeCustomizationService,
        private helper: AuthServiceHelper
    ) {
        this.currentUser$.subscribe(user => {
            this.refresher.stop();
            if (user) {
                this.refresher.run();
            }
        });
    }

    fetchCurrentUser(silent = false, fetchPolicy: FetchPolicy = 'cache-first'): Promise<CurrentUser> {
        if (!this.localStorage.getItem('apd_access_token')) {
            this.setCurrentUser(null);
            return Promise.resolve(null);
        }
        this.currentFAQUrl$ = this.api.query<currentFAQUrl>({query: faqUrlQuery}).pipe(map(r => r.currentFAQUrl));
        return this.api
            .query<currentUser>({
                query: currentUserQuery,
                fetchPolicy,
            })
            .toPromise()
            .then(r => r.currentUser)
            .then(user => (silent ? user : this.setCurrentUser(user)));
    }

    setCurrentUser(user: CurrentUser): CurrentUser {
        this.loggedId = Boolean(user);

        this.demo.isDemoUser = user && user.isDemoAccount;

        this.currentUser = user;
        this.currentUserId = user ? user.id : undefined;
        if (user) {
            if (user.roles.includes(UserRoles.NetworkPartner)) {
                user = cloneDeep(user);
                user.permissions.canViewOldDashboard = false;
            }
        }
        (this.currentUser$ as BehaviorSubject<CurrentUser>).next(user);
        return user;
    }

    init(): Promise<CurrentUser | null> {
        return this.fetchCurrentUser().then(
            () =>
                this.currentUser$
                    .pipe(
                        take(1),
                        tap(user => {
                            this.helper.setCurrentUser(user);
                            if (!user || !user.themeMode) {
                                return;
                            }
                            this.themeCustom.setThemeMode(user.themeMode);
                        })
                    )
                    .toPromise(),
            e => {
                if (e instanceof AuthorizationError) {
                    this.setCurrentUser(null);
                    return null;
                }

                throw e;
            }
        );
    }

    isLoggedIn() {
        return this.loggedId;
    }

    getCurrentUserLastValue(): CurrentUser {
        return this.currentUser;
    }

    getCurrentUserWithoutRoles(): Promise<CurrentUser> {
        // skip admins, accountants, support, etc.
        const isNotSystemUser = (user: CurrentUser): boolean => user.roles.length === 0;

        return this.currentUser$.pipe(filter<CurrentUser>(Boolean), filter(isNotSystemUser), take(1)).toPromise();
    }

    signIn2Fa({email, password, twoFactorEmailCode, twoFactorOneTimePassword}) {
        return this.api
            .mutate<signInV2, signInV2Variables>({
                mutation: signInV2Mutation,
                variables: {
                    email,
                    password,
                    twoFactorEmailCode: twoFactorEmailCode ? twoFactorEmailCode : null,
                    twoFactorOneTimePassword: twoFactorOneTimePassword ? twoFactorOneTimePassword : null,
                },
                skipBeforeMutationValidation: true,
            })
            .pipe(
                map(r => r.signInV2),
                tap(signInSession => {
                    if (!signInSession.session) {
                        this.twoFaEmailCode$.next(signInSession.twoFactorChallengeType === TwoFactorChallengeType.EMAIL);
                        this.twoFaOneTimePassword$.next(signInSession.twoFactorChallengeType === TwoFactorChallengeType.ONE_TIME_PASSWORD);
                        return;
                    }
                    this.storeSessionAccessTokens(signInSession.session);
                }),
                switchMap(signV2 => (signV2.session !== null ? this.fetchCurrentUser(true) : null))
            );
    }

    storeSessionAccessTokens({accessToken, refreshToken}: signInV2_signInV2_session) {
        this.localStorage.setItem('apd_access_token', accessToken);
        this.localStorage.setItem('apd_refresh_token', refreshToken);
    }

    demoSignIn() {
        return this.api
            .mutate<demoSignIn>({
                mutation: demoSignInMutation,
                skipBeforeMutationValidation: true,
            })
            .pipe(
                map(r => r.demoSignIn),
                tap(({accessToken, refreshToken}) => {
                    this.localStorage.setItem('apd_access_token', accessToken);
                    this.localStorage.setItem('apd_refresh_token', refreshToken);
                })
            );
    }

    remindPassword(email: string) {
        return this.api
            .mutate<remindPassword>({
                mutation: remindPasswordMutation,
                variables: {email},
            })
            .pipe(map(r => r.remindPassword));
    }

    signUp(params: signUpVariables): Observable<signUp_signUp> {
        return this.api
            .mutate<signUp>({
                mutation: signUpMutation,
                variables: params,
            })
            .pipe(map(r => r.signUp));
    }

    resendConfirmationEmail(email: string) {
        return this.api
            .mutate<resendConfirmationEmail>({
                mutation: resendConfirmationEmailMutation,
                variables: {email},
            })
            .pipe(map(r => r.resendConfirmationEmail));
    }

    signOut() {
        return this.api
            .mutate({
                mutation: signOutMutation,
                variables: {
                    token: this.localStorage.getItem('apd_refresh_token') || null,
                },
                skipBeforeMutationValidation: true,
            })
            .pipe(
                switchMap(() => this.cleanApiCache.cleanLocalCache()),
                tap(() => {
                    this.localStorage.setItem('apd_access_token', '');
                    this.localStorage.setItem('apd_refresh_token', '');
                    this.clearSavedAppInfo();
                }),
                tap(() => {
                    this.afterSignOut.forEach(f => f());
                })
            );
    }

    clearSavedAppInfo() {
        this.localStorage.removeItem('user_id');
        this.localStorage.removeItem('app_id');
    }

    addAfterSignOutCallback(f: () => void) {
        this.afterSignOut.push(f);
    }
}
